@if (isLoading) {
<lib-loading-indicator [isLoading]="isLoading"></lib-loading-indicator>
}
<!-- The same page frame and panels as the latency policy form, so the two policy pages read as one product. -->
<div class="nf-policy-form-wrap" id="OfflinePolicyFormPage" (keyup.escape)="close()">
    <div class="nf-policy-form">
        <div class="nf-policy-form__header">
            <div class="nf-policy-form__title-row">
                <div
                    class="nf-policy-form__back"
                    id="OfflinePolicyBack"
                    (click)="close()"
                    matTooltip="Back to Offline Policies"
                    matTooltipPosition="below"
                >
                    <div class="nf-policy-form__back-icon icon-back-arrow"></div>
                </div>
                <div class="nf-policy-form__title-text">
                    <span class="nf-policy-form__title">{{ title }}</span>
                </div>
            </div>
        </div>

        <div class="nf-panel">
            <div class="nf-panel__header">
                <div class="nf-panel__heading">
                    <h2 class="nf-panel__title">What it applies to</h2>
                    <p class="nf-panel__subtitle">The component this policy decides offline alerts for.</p>
                </div>
            </div>
            <div class="nf-panel__body">
                <div class="stacked-fields">
                    @if (isEditing) {
                    <div class="field-row">
                        <div class="field-label">Scope</div>
                        <span class="field-value" id="OfflinePolicyScopeValue">{{ lockedScope }}</span>
                        <p class="offline-policy-note" id="OfflinePolicyScopeLockedNote">
                            What a policy applies to cannot be changed. To point one at something else, delete this
                            policy and create another.
                        </p>
                    </div>
                    } @else {
                    <!-- Only when the service offers more than one component type; with one there is nothing to choose. -->
                    @if (componentTypeOptions.length > 1) {
                    <div class="field-row">
                        <div class="field-label">Component type <span class="field-label-suffix">REQUIRED</span></div>
                        <div class="offline-policy-options" id="OfflinePolicyComponentType" role="radiogroup" aria-label="Component type">
                            @for (option of componentTypeOptions; track option) {
                            <button
                                type="button"
                                class="offline-policy-option"
                                id="OfflinePolicyComponentType{{ option }}"
                                role="radio"
                                [attr.aria-checked]="componentType === option"
                                [class.selected]="componentType === option"
                                (click)="setComponentType(option)"
                            >
                                <span class="offline-policy-option-mark" aria-hidden="true"></span>
                                <span class="offline-policy-option-text">
                                    <span class="offline-policy-option-title">{{ componentTypeLabel(option) }}</span>
                                    <span class="offline-policy-option-detail">{{ offlineTypeDetail(option) }}</span>
                                </span>
                            </button>
                            }
                        </div>
                    </div>
                    }
                    <div class="field-row offline-policy-scope-field">
                        <div class="field-label" [class.offline-policy-label-invalid]="!!componentError">
                            {{ componentLabel }}
                            <span class="field-label-suffix">REQUIRED</span>
                        </div>
                        <!-- Searches the controller as the reader types. Without a list to search, the box takes the id as text. -->
                        <p-autoComplete
                            class="nf-autocomplete"
                            [class.nf-autocomplete--invalid]="!!componentError"
                            [inputStyleClass]="'form-field-input'"
                            inputId="OfflinePolicyComponent"
                            [ngModel]="componentPick"
                            (ngModelChange)="takeComponentPick($event)"
                            [suggestions]="componentSuggestions"
                            (completeMethod)="searchComponents($event.query)"
                            optionLabel="name"
                            [forceSelection]="canPick"
                            [dropdown]="canPick"
                            [minLength]="searchMinLength"
                            [delay]="searchDelay"
                            [showClear]="true"
                            [placeholder]="componentPlaceholder"
                            [emptyMessage]="canPick ? 'No component of this kind matches' : ''"
                            [ariaLabel]="componentLabel"
                            appendTo="body"
                            [overlayOptions]="pickerOverlay"
                            [disabled]="!componentType"
                        ></p-autoComplete>
                        @if (componentError) {
                        <p class="offline-policy-field-error" id="OfflinePolicyComponentError" role="alert">
                            {{ componentError }}
                        </p>
                        }
                        <p class="offline-policy-note" id="OfflinePolicyScopeNote">
                            To set the ladder for every {{ componentLabel.toLowerCase() }} in the network, use the Network
                            Policy section on the Offline Policies page.
                        </p>
                    </div>
                    @if (needsRole) {
                    <div class="field-row">
                        <div class="field-label" [class.offline-policy-label-invalid]="!!roleError">
                            Role <span class="field-label-suffix">REQUIRED</span>
                        </div>
                        <div
                            class="offline-policy-options"
                            [class.offline-policy-options--invalid]="!!roleError"
                            id="OfflinePolicyRole"
                            role="radiogroup"
                            aria-label="Role"
                        >
                            @for (option of roleOptions; track option.value) {
                            <button
                                type="button"
                                class="offline-policy-option"
                                id="OfflinePolicyRole{{ option.value }}"
                                role="radio"
                                [attr.aria-checked]="role === option.value"
                                [class.selected]="role === option.value"
                                (click)="setRole(option.value)"
                            >
                                <span class="offline-policy-option-mark" aria-hidden="true"></span>
                                <span class="offline-policy-option-text">
                                    <span class="offline-policy-option-title">{{ option.label }}</span>
                                    <span class="offline-policy-option-detail">{{ option.detail }}</span>
                                </span>
                            </button>
                            }
                        </div>
                        @if (roleError) {
                        <p class="offline-policy-field-error" id="OfflinePolicyRoleError" role="alert">{{ roleError }}</p>
                        }
                        <p class="offline-policy-note" id="OfflinePolicyRoleNote">
                            The role is set when an outage begins, so one {{ componentLabel.toLowerCase() }} can have a
                            policy for each role.
                        </p>
                    </div>
                    } }
                </div>
            </div>
        </div>

        <div class="nf-panel">
            <div class="nf-panel__header">
                <div class="nf-panel__heading">
                    <h2 class="nf-panel__title">Offline alerts</h2>
                    <p class="nf-panel__subtitle">Whether to alert, and how long offline before each severity.</p>
                </div>
            </div>
            <div class="nf-panel__body">
                @if (ladderLockedReason) {
                <p class="offline-policy-note offline-policy-note--locked" id="OfflinePolicyLadderLocked">
                    {{ ladderLockedReason }}
                </p>
                }
                <div class="stacked-fields">
                    <div class="field-row">
                        <div class="field-label" [class.offline-policy-label-invalid]="!!errorFor('$.enabled')">Alerting</div>
                        <div
                            class="offline-policy-options"
                            [class.offline-policy-options--invalid]="!!errorFor('$.enabled')"
                            id="OfflinePolicyEnabled"
                            role="radiogroup"
                            aria-label="Alerting"
                        >
                            <button
                                type="button"
                                class="offline-policy-option"
                                id="OfflinePolicyEnabledOn"
                                role="radio"
                                [attr.aria-checked]="enabled"
                                [class.selected]="enabled"
                                [disabled]="!canEditLadder"
                                (click)="setEnabled(true)"
                            >
                                <span class="offline-policy-option-mark" aria-hidden="true"></span>
                                <span class="offline-policy-option-text">
                                    <span class="offline-policy-option-title">On</span>
                                    <span class="offline-policy-option-detail">Alert when it stays offline longer than the ladder below.</span>
                                </span>
                            </button>
                            <button
                                type="button"
                                class="offline-policy-option"
                                id="OfflinePolicyEnabledOff"
                                role="radio"
                                [attr.aria-checked]="!enabled"
                                [class.selected]="!enabled"
                                [disabled]="!canEditLadder"
                                (click)="setEnabled(false)"
                            >
                                <span class="offline-policy-option-mark" aria-hidden="true"></span>
                                <span class="offline-policy-option-text">
                                    <span class="offline-policy-option-title">Off</span>
                                    <span class="offline-policy-option-detail">
                                        @if (isNetworkDefault) { Never alert when a {{ componentLabel.toLowerCase() }} goes offline. }
                                        @else { Never alert when this {{ componentLabel.toLowerCase() }} goes offline. }
                                    </span>
                                </span>
                            </button>
                        </div>
                        @if (errorFor('$.enabled')) {
                        <p class="offline-policy-field-error" id="OfflinePolicyEnabledError" role="alert">{{ errorFor('$.enabled') }}</p>
                        }
                    </div>

                    @if (enabled) {
                    <div class="field-row">
                        <div class="field-label" [class.offline-policy-label-invalid]="hasLadderError">Ladder</div>
                        <app-offline-ladder-fields
                            idPrefix="OfflinePolicyLadder"
                            orientation="horizontal"
                            [ladder]="ladder"
                            [placeholders]="placeholders"
                            [errors]="rungErrors"
                            [allInvalid]="!!errorFor('$.ladder')"
                            [disabled]="!canEditLadder"
                            (ladderChange)="setLadder($event)"
                            (rungBlur)="rungBlurred()"
                            (invalidChange)="ladderInvalid = $event"
                        ></app-offline-ladder-fields>
                        @if (errorFor('$.ladder')) {
                        <p class="offline-policy-field-error" id="OfflinePolicyLadderError" role="alert">{{ errorFor('$.ladder') }}</p>
                        }
                        <p class="offline-policy-note" id="OfflinePolicyLadderNote">
                            @if (!ladderMayBeEmpty) { An empty box is not on the ladder. Set at least one severity. }
                            @else { Leave every box empty to use the ladder from {{ inheritedFrom }}, shown in grey. }
                            Each severity must start later than the one above it.
                        </p>
                    </div>
                    }
                </div>
            </div>

            @if (unclaimedErrors.length > 0) {
            <ul class="offline-policy-field-error" id="OfflinePolicyOtherErrors" role="alert">
                @for (error of unclaimedErrors; track error) {
                <li>{{ error }}</li>
                }
            </ul>
            } @if (saveError) {
            <div class="offline-policy-errors" id="OfflinePolicySaveError" role="alert">
                <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                <div>{{ saveError }}</div>
            </div>
            }

            @if (saved) {
            <div class="offline-policy-warnings" id="OfflinePolicySavedWarnings" role="status">
                <i class="pi pi-info-circle" aria-hidden="true"></i>
                <div>
                    <div class="offline-policy-warnings__title">The policy was saved, with a warning.</div>
                    @for (warning of savedWarnings; track warning) {
                    <div>{{ warning }}</div>
                    }
                </div>
            </div>
            }

            <div class="nf-panel__actions">
                <button type="button" class="nf-btn nf-btn--ghost" id="OfflinePolicyCancel" (click)="close()">
                    {{ saved ? 'Done' : 'Cancel' }}
                </button>
                <button
                    type="button"
                    class="nf-btn nf-btn--primary"
                    id="OfflinePolicySave"
                    [disabled]="processing || saved"
                    (click)="save()"
                >
                    {{ processing ? 'Saving…' : isEditing ? 'Save' : 'Create' }}
                </button>
            </div>
        </div>
    </div>
</div>
